import { useState } from "react";
import { Button, useNotify } from "react-admin";
import { Grid, IconButton, MenuItem, TextField, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import Edit from "@mui/icons-material/Edit";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import FormModal from "server/components/Modals/FormModal";
import dataProvider from "server/dataProvider";
import { studentRequests } from "api/studentApi";
import { CourseChoices } from "utils/choices";

const emptyRow = {
  name: "",
  lecturesHours: "",
  exercisesHours: "",
  independentWorkHours: "",
  course: "",
  grade: "",
};

const ExternalDisciplineRows = () => {
  const { control } = useFormContext();
  const { fields, append, remove } = useFieldArray({ control, name: "disciplines" });

  return (
    <Grid
      container
      spacing={2}
    >
      {fields.map((field, idx) => (
        <Grid
          item
          xs={12}
          key={field.id}
        >
          <Grid
            container
            spacing={2}
            alignItems="center"
          >
            <Grid
              item
              xs={12}
              md={3}
            >
              <Controller
                name={`disciplines.${idx}.name`}
                control={control}
                render={({ field: rowField }) => (
                  <TextField
                    {...rowField}
                    label="Наименование"
                    fullWidth
                    required
                  />
                )}
              />
            </Grid>
            <Grid
              item
              xs={6}
              md={1.5}
            >
              <Controller
                name={`disciplines.${idx}.lecturesHours`}
                control={control}
                render={({ field: rowField }) => (
                  <TextField
                    {...rowField}
                    label="Лекции"
                    type="number"
                    InputProps={{ inputProps: { min: 0 } }}
                    fullWidth
                  />
                )}
              />
            </Grid>
            <Grid
              item
              xs={6}
              md={1.5}
            >
              <Controller
                name={`disciplines.${idx}.exercisesHours`}
                control={control}
                render={({ field: rowField }) => (
                  <TextField
                    {...rowField}
                    label="Упражнения"
                    type="number"
                    InputProps={{ inputProps: { min: 0 } }}
                    fullWidth
                  />
                )}
              />
            </Grid>
            <Grid
              item
              xs={6}
              md={2}
            >
              <Controller
                name={`disciplines.${idx}.independentWorkHours`}
                control={control}
                render={({ field: rowField }) => (
                  <TextField
                    {...rowField}
                    label="Извънаудиторна заетост"
                    type="number"
                    InputProps={{ inputProps: { min: 0 } }}
                    fullWidth
                  />
                )}
              />
            </Grid>
            <Grid
              item
              xs={6}
              md={1.5}
            >
              <Controller
                name={`disciplines.${idx}.course`}
                control={control}
                render={({ field: rowField }) => (
                  <TextField
                    {...rowField}
                    select
                    label="Курс"
                    fullWidth
                    required
                  >
                    {CourseChoices.map(choice => (
                      <MenuItem
                        key={choice.id}
                        value={choice.id}
                      >
                        {choice.name}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
            <Grid
              item
              xs={6}
              md={1.5}
            >
              <Controller
                name={`disciplines.${idx}.grade`}
                control={control}
                render={({ field: rowField }) => (
                  <TextField
                    {...rowField}
                    label="Оценка"
                    type="number"
                    InputProps={{ inputProps: { min: 2, max: 6, step: 0.01 } }}
                    fullWidth
                  />
                )}
              />
            </Grid>
            <Grid
              item
              xs={12}
              md={1}
            >
              <IconButton
                aria-label="Премахни"
                onClick={() => remove(idx)}
              >
                <DeleteIcon />
              </IconButton>
            </Grid>
          </Grid>
        </Grid>
      ))}
      <Grid
        item
        xs={12}
      >
        <Button
          label="Добави дисциплина"
          startIcon={<AddIcon />}
          onClick={() => append(emptyRow)}
        />
      </Grid>
    </Grid>
  );
};

const ExternalDisciplinesModal = ({ record, onSaved }) => {
  const notify = useNotify();
  const [initialRows, setInitialRows] = useState(null);

  const fetchRows = async () => {
    const { data } = await dataProvider.customRequest(
      studentRequests.getOtherUniversityExternalDisciplines(record?.id),
      null,
      "GET",
    );
    setInitialRows(
      (data ?? []).map(row => ({
        name: row.name,
        lecturesHours: row.lecturesHours ?? "",
        exercisesHours: row.exercisesHours ?? "",
        independentWorkHours: row.independentWorkHours ?? "",
        course: row.course,
        grade: row.grade ?? "",
      })),
    );
  };

  const toNullableNumber = value => (value === "" || value == null ? null : Number(value));

  const handleSave = async values => {
    const rows = values.disciplines ?? [];
    if (rows.some(row => !row.name?.trim() || !row.course)) {
      notify("Моля попълнете наименование и курс за всяка дисциплина!", { type: "error" });
      return false;
    }
    if (rows.some(row => row.grade !== "" && row.grade != null && (Number(row.grade) < 2 || Number(row.grade) > 6))) {
      notify("Оценката трябва да е между 2 и 6.", { type: "error" });
      return false;
    }
    const payload = {
      disciplines: rows.map(row => ({
        name: row.name.trim(),
        lecturesHours: toNullableNumber(row.lecturesHours),
        exercisesHours: toNullableNumber(row.exercisesHours),
        independentWorkHours: toNullableNumber(row.independentWorkHours),
        course: Number(row.course),
        grade: toNullableNumber(row.grade),
      })),
    };
    await dataProvider.customRequest(
      studentRequests.updateOtherUniversityExternalDisciplines(record?.id),
      payload,
      "PUT",
    );
    notify("Успешно записани дисциплини извън учебния план", { type: "success" });
    onSaved?.();
  };

  const handleOpen = async () => {
    try {
      await fetchRows();
    } catch (err) {
      console.error("Error fetching external disciplines", err);
      notify("Грешка при зареждане на дисциплините. Опитайте да презаредите страницата.", { type: "error" });
      return false;
    }
  };

  return (
    <FormModal
      titleContent="Дисциплини извън учебния план"
      dialogSize="lg"
      onConfirm={handleSave}
      onOpen={handleOpen}
      resetProps={() => setInitialRows(null)}
      defaultValues={{ disciplines: initialRows?.length ? initialRows : [emptyRow] }}
      triggerButton={
        <Button
          label="Дисциплини извън учебния план"
          variant="contained"
          startIcon={<Edit />}
        />
      }
      dialogProps={{
        sx: {
          "& .MuiDialog-paper": {
            maxHeight: "80%",
            minWidth: "70%",
          },
        },
      }}
    >
      <Typography sx={{ mb: 2 }}>
        Дисциплини, изучавани в друг университет, които не са част от учебния план.
      </Typography>
      <ExternalDisciplineRows />
    </FormModal>
  );
};

export default ExternalDisciplinesModal;
